import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { InviteModal } from '@/components/InviteModal';
import { toast } from 'sonner';
import { MessageCircleQuestion } from 'lucide-react';

type Step = 'user_id' | 'group_choice' | 'create_group' | 'join_group';

function ReportProblemLink({ step, userId, inviteCode }: { step: Step; userId?: string; inviteCode?: string }) {
  const { t } = useLanguage();
  
  const getSubject = () => {
    return encodeURIComponent('💯 drinkov: problem');
  };
  
  const getBody = () => {
    const stepNames: Record<Step, string> = {
      user_id: 'Nickname selection',
      group_choice: 'Create/Join choice',
      create_group: 'Create challenge',
      join_group: 'Join challenge',
    };
    const lines = [
      `Screen: ${stepNames[step]}`,
      userId ? `User ID: ${userId}` : null,
      inviteCode ? `Invite Code: ${inviteCode}` : null,
      '',
      'Describe the issue:',
      '',
    ].filter(Boolean);
    return encodeURIComponent(lines.join('\n'));
  };
  
  return (
    <a 
      href={`mailto:peter@tugedr.com?subject=${getSubject()}&body=${getBody()}`}
      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
    >
      <MessageCircleQuestion className="h-3.5 w-3.5" />
      {t('reportProblem')}
    </a>
  );
}

function CompletedChallengeSummary({ 
  group, 
  totalDrinks, 
  longestStreak 
}: { 
  group: { challenge_start: string; challenge_end: string }; 
  totalDrinks: number; 
  longestStreak: number;
}) {
  const { t, language } = useLanguage();

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(language === 'sk' ? 'sk-SK' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="mb-6 animate-fade-in">
      <div className="text-center mb-4">
        <h2 className="text-lg font-serif font-semibold text-foreground">
          {t('completedSummaryTitle')}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t('completedSummaryDesc')}
        </p>
      </div>
      
      <div className="bg-muted/30 border border-border/50 rounded-lg p-4 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('completedPeriod')}</span>
          <span className="font-medium text-foreground/80">
            {formatDate(group.challenge_start)} – {formatDate(group.challenge_end)}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('completedTotalDrinks')}</span>
          <span className="font-medium text-foreground/80">{totalDrinks}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('completedLongestStreak')}</span>
          <span className="font-medium text-foreground/80">{longestStreak} {t('dashboardDays')}</span>
        </div>
        <div className="flex items-center justify-between text-sm pt-1 border-t border-border/30">
          <span className="text-muted-foreground">{t('completedStatus')}</span>
          <span className="text-xs font-medium px-2 py-0.5 bg-primary/10 text-primary rounded-full">
            {t('completedLabel')}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function Onboarding() {
  const { user, profile, completedChallenge, refreshProfile, refreshMembership, clearCompletedChallenge } = useAuth();
  const { t } = useLanguage();
  const [step, setStep] = useState<Step>('user_id');
  const [userId, setUserId] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdInviteToken, setCreatedInviteToken] = useState<string | null>(null);
  const [nicknameError, setNicknameError] = useState<string | null>(null);

  // Reactively set the correct step based on profile state
  useEffect(() => {
    if (profile?.user_id) {
      // User has nickname, go to group selection (unless already in create/join flow)
      setStep(prev => prev === 'user_id' ? 'group_choice' : prev);
    } else {
      // User needs to set nickname
      setStep('user_id');
    }
  }, [profile?.user_id]);

  const handleSetUserId = async (e: React.FormEvent) => {
    e.preventDefault();
    setNicknameError(null);
    
    if (userId.length < 3 || userId.length > 10) {
      setNicknameError(t('onboardingNicknameShort'));
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(userId)) {
      setNicknameError(t('onboardingNicknameInvalid'));
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: existing } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (existing) {
        setNicknameError(t('onboardingNicknameTaken'));
        setIsSubmitting(false);
        return;
      }

      const { error } = await supabase
        .from('profiles')
        .update({ user_id: userId })
        .eq('id', user!.id);

      if (error) {
        setNicknameError(t('onboardingNicknameTaken'));
      } else {
        await refreshProfile();
        setStep('group_choice');
        toast.success(t('onboardingNicknameSuccess'));
      }
    } catch {
      setNicknameError(t('authUnexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateGroup = async () => {
    setIsSubmitting(true);

    try {
      // Generate invite code (internal, creator-only)
      const { data: codeData, error: codeError } = await supabase.rpc('generate_invite_code');
      
      if (codeError || !codeData) {
        toast.error(t('onboardingGenerateError'));
        setIsSubmitting(false);
        return;
      }

      const code = codeData as string;

      // Calculate challenge dates (start at next midnight)
      const now = new Date();
      const challengeStart = new Date(now);
      challengeStart.setDate(challengeStart.getDate() + 1);
      challengeStart.setHours(0, 0, 0, 0);
      
      const challengeEnd = new Date(challengeStart);
      challengeEnd.setDate(challengeEnd.getDate() + 365);

      // Create group and membership atomically using secure RPC
      const { data: groupData, error: groupError } = await supabase
        .rpc('create_group_with_membership', {
          p_invite_code: code,
          p_challenge_start: challengeStart.toISOString(),
          p_challenge_end: challengeEnd.toISOString(),
        })
        .single();

      if (groupError || !groupData) {
        const errorMessage = groupError?.message || 'Failed to create group';
        if (errorMessage.includes('already has an active group')) {
          toast.error(t('onboardingAlreadyActive'));
        } else {
          toast.error(t('onboardingCreateFailed'));
        }
        setIsSubmitting(false);
        return;
      }

      // Generate an invite token for sharing (not the internal code)
      const { data: tokenData, error: tokenError } = await supabase.rpc('generate_invite_token', {
        p_group_id: groupData.group_id,
      });

      if (tokenError || !tokenData) {
        // Fallback: still show the modal but with the internal code for the creator
        toast.error(t('authUnexpectedError'));
        setIsSubmitting(false);
        return;
      }

      setCreatedInviteToken(tokenData as string);
      clearCompletedChallenge();
      // NOTE: Do NOT call refreshMembership() here - it would update AuthContext
      // and cause Index.tsx to switch to Dashboard, unmounting this component
      // before the invite modal can render. refreshMembership() is called
      // when the user closes the invite modal.
      // NOTE: No toast shown here - the Invite Modal provides the success feedback
    } catch {
      toast.error(t('authUnexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleJoinGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const code = inviteCode.toUpperCase().trim();
    
    if (code.length < 6 || code.length > 8) {
      toast.error(t('onboardingInviteCodeLength'));
      return;
    }

    setIsSubmitting(true);

    try {
      let groupId: string | null = null;

      // Try token lookup first (8 chars), then fall back to invite code (6 chars)
      if (code.length === 8) {
        const { data: tokenData, error: tokenError } = await supabase
          .rpc('lookup_group_by_invite_token', { p_token: code })
          .maybeSingle();

        if (!tokenError && tokenData) {
          if (tokenData.status === 'completed') {
            toast.error(t('onboardingChallengeEnded'));
            setIsSubmitting(false);
            return;
          }
          groupId = tokenData.id;
        }
      }

      // Fall back to invite code lookup (for backward compat / creator codes)
      if (!groupId) {
        const { data: codeData, error: codeError } = await supabase
          .rpc('lookup_group_by_invite_code', { code })
          .maybeSingle();

        if (codeError || !codeData) {
          toast.error(t('onboardingInvalidCode'));
          setIsSubmitting(false);
          return;
        }

        if (codeData.status === 'completed') {
          toast.error(t('onboardingChallengeEnded'));
          setIsSubmitting(false);
          return;
        }
        groupId = codeData.id;
      }

      if (!groupId) {
        toast.error(t('onboardingInvalidCode'));
        setIsSubmitting(false);
        return;
      }

      // Create membership
      const { error: membershipError } = await supabase
        .from('memberships')
        .insert({
          user_id: user!.id,
          group_id: groupId,
          role: 'member',
        });

      if (membershipError) {
        if (membershipError.message.includes('duplicate key')) {
          toast.error(t('onboardingAlreadyInGroup'));
        } else {
          toast.error(t('onboardingJoinFailed'));
        }
        setIsSubmitting(false);
        return;
      }

      clearCompletedChallenge();
      await refreshMembership();
      toast.success(t('onboardingJoinSuccess'));
    } catch {
      toast.error(t('authUnexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseInviteModal = async () => {
    // Now that modal is dismissed, update membership state
    // This will cause Index.tsx to route to Dashboard
    await refreshMembership();
  };

  // If group was just created, show invite modal
  if (createdInviteToken) {
    return (
      <InviteModal 
        token={createdInviteToken} 
        onClose={handleCloseInviteModal}
      />
    );
  }

  return (
    <div className="min-h-screen-safe flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-serif">
            {step === 'user_id' && t('onboardingNicknameTitle')}
            {step === 'group_choice' && t('onboardingJoinTitle')}
            {step === 'create_group' && t('onboardingCreateTitle')}
            {step === 'join_group' && t('onboardingJoinCodeTitle')}
          </CardTitle>
          {(step !== 'create_group') && (
            <CardDescription>
              {step === 'user_id' && t('onboardingNicknameWarning')}
              {step === 'group_choice' && t('onboardingJoinDesc')}
              {step === 'join_group' && t('onboardingJoinCodeDesc')}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {step === 'user_id' && (
            <form onSubmit={handleSetUserId} className="space-y-4">
              <div className="space-y-2">
                <Input
                  id="userId"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder={t('onboardingNicknamePlaceholder')}
                  maxLength={10}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {t('onboardingNicknameHint')}
                </p>
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? t('onboardingChecking') : t('continue')}
              </Button>
              {nicknameError && (
                <p className="text-sm text-destructive text-center mt-2">
                  {nicknameError}
                </p>
              )}
            </form>
          )}

          {step === 'group_choice' && (
            <div className="space-y-4">
              {completedChallenge && (
                <CompletedChallengeSummary 
                  group={completedChallenge.group}
                  totalDrinks={completedChallenge.totalDrinks}
                  longestStreak={completedChallenge.longestStreak}
                />
              )}
              <Button 
                onClick={() => setStep('create_group')} 
                className="w-full h-16 text-lg"
              >
                {t('onboardingCreateBtn')}
              </Button>
              <Button 
                onClick={() => setStep('join_group')} 
                variant="outline"
                className="w-full h-16 text-lg"
              >
                {t('onboardingJoinBtn')}
              </Button>
            </div>
          )}

          {step === 'create_group' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('onboardingCreateInfo')}
              </p>
              <Button 
                onClick={handleCreateGroup} 
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('onboardingCreating') : t('onboardingCreateChallenge')}
              </Button>
              <Button 
                onClick={() => setStep('group_choice')} 
                variant="ghost"
                className="w-full"
              >
                {t('back')}
              </Button>
            </div>
          )}

          {step === 'join_group' && (
            <form onSubmit={handleJoinGroup} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="inviteCode">{t('onboardingInviteCode')}</Label>
                <Input
                  id="inviteCode"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  placeholder={t('onboardingInviteCodePlaceholder')}
                  maxLength={8}
                  className="text-center text-2xl font-mono tracking-widest"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? t('onboardingJoining') : t('onboardingJoinChallenge')}
              </Button>
              <Button 
                type="button"
                onClick={() => setStep('group_choice')} 
                variant="ghost"
                className="w-full"
              >
                {t('back')}
              </Button>
            </form>
          )}
        </CardContent>
        <CardFooter className="justify-center">
          <ReportProblemLink step={step} userId={user?.id} inviteCode={step === 'join_group' ? inviteCode : createdInviteToken || undefined} />
        </CardFooter>
      </Card>
    </div>
  );
}
